import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
  Badge,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaSave,
  FaDownload,
  FaCalendarCheck,
  FaUserGraduate,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../../components/common/Header';
import AttendanceService from '../../../services/attendanceService';
import StudentHistoryModal from './components/StudentHistoryModal';

const DailyAttendance = () => {
  const navigate = useNavigate();
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      setLoadingBatches(true);
      const response = await AttendanceService.getBatches();
      if (response.success) {
        setBatches(response.data);
      }
    } catch (error) {
      console.error('Failed to load batches:', error);
      toast.error('Failed to load batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const loadStudents = async (batchId, date) => {
    if (!batchId) return;
    
    try {
      setLoading(true);
      const response = await AttendanceService.getAttendanceByDate(batchId, date);
      if (response.success) {
        const studentsData = response.data.students;
        setStudents(studentsData);
        
        const initialStatus = {};
        studentsData.forEach(student => {
          initialStatus[student.id] = student.status || 'present';
        });
        setAttendanceStatus(initialStatus);
      }
    } catch (error) {
      toast.error('Failed to load students');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchChange = (e) => {
    const batchId = e.target.value;
    setSelectedBatch(batchId);
    if (batchId && selectedDate) {
      loadStudents(batchId, selectedDate);
    } else {
      setStudents([]);
      setAttendanceStatus({});
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    if (selectedBatch && date) {
      loadStudents(selectedBatch, date);
    }
  };

  const toggleStatus = (studentId) => {
    const newStatus = attendanceStatus[studentId] === 'present' ? 'absent' : 'present';
    setAttendanceStatus(prev => ({
      ...prev,
      [studentId]: newStatus
    }));
  };

  const handleSave = async () => {
    if (!selectedBatch || !selectedDate) {
      toast.error('Please select batch and date');
      return;
    }

    try {
      setSaving(true);
      const response = await AttendanceService.saveAttendance(
        selectedBatch,
        selectedDate,
        attendanceStatus
      );
      
      if (response.success) {
        toast.success(`Attendance saved! ${response.data.total} records updated`);
        loadStudents(selectedBatch, selectedDate);
      } else {
        toast.error(response.message || 'Failed to save attendance');
      }
    } catch (error) {
      toast.error('Error saving attendance');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    if (!selectedBatch) {
      toast.error('Please select a batch first');
      return;
    }

    try {
      await AttendanceService.exportAttendance(selectedBatch);
      toast.success('Export started! Check your downloads folder.');
    } catch (error) {
      toast.error('Failed to export attendance');
      console.error(error);
    }
  };

  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
  };

  const getStatusBadge = (status) => {
    if (status === 'present') {
      return <Badge bg="success"><FaCheckCircle className="me-1" /> Present</Badge>;
    }
    return <Badge bg="danger"><FaTimesCircle className="me-1" /> Absent</Badge>;
  };

  return (
    <div>
      <Header />
      <Container fluid className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Button
              variant="link"
              className="text-decoration-none mb-2 p-0"
              onClick={() => navigate('/admin')}
            >
              <FaArrowLeft className="me-2" /> Back to Dashboard
            </Button>
            <h2 className="mb-0">📋 Mark Daily Attendance</h2>
            <p className="text-muted">Select batch and date to mark attendance. All students are present by default. Check the checkbox for absent students.</p>
          </div>
        </div>

        <Row>
          <Col lg={12}>
            {/* Filters Card */}
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">🎯 Select Batch & Date</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>Select Batch</Form.Label>
                      <Form.Select
                        value={selectedBatch}
                        onChange={handleBatchChange}
                        disabled={loadingBatches}
                      >
                        <option value="">-- Select a Batch --</option>
                        {batches.map(batch => (
                          <option key={batch.id} value={batch.id}>
                            {batch.name} ({batch.status})
                          </option>
                        ))}
                      </Form.Select>
                      {loadingBatches && (
                        <div className="mt-2">
                          <Spinner animation="border" size="sm" /> Loading batches...
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Select Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={selectedDate}
                        onChange={handleDateChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <div className="d-flex gap-2 w-100">
                      <Button
                        variant="success"
                        onClick={handleSave}
                        disabled={saving || !selectedBatch || students.length === 0}
                        className="w-50"
                      >
                        <FaSave className="me-2" />
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button
                        variant="info"
                        onClick={handleExport}
                        disabled={!selectedBatch || students.length === 0}
                        className="w-50"
                      >
                        <FaDownload className="me-2" />
                        Export
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Students Table Card */}
            <Card className="shadow-sm">
              <Card.Header className="bg-secondary text-white">
                <h5 className="mb-0">
                  <FaUserGraduate className="me-2" />
                  Students List
                  {students.length > 0 && (
                    <Badge bg="light" text="dark" className="ms-2">
                      {students.length} Students
                    </Badge>
                  )}
                </h5>
              </Card.Header>
              <Card.Body>
                {loading && (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading students...</p>
                  </div>
                )}

                {!loading && students.length === 0 && selectedBatch && (
                  <Alert variant="warning" className="text-center">
                    No students found in this batch. Please add students to the batch first.
                  </Alert>
                )}

                {!loading && students.length === 0 && !selectedBatch && (
                  <Alert variant="info" className="text-center">
                    Please select a batch to view students.
                  </Alert>
                )}

                {!loading && students.length > 0 && (
                  <div className="table-responsive">
                    <Table hover className="attendance-table">
                      <thead className="table-dark">
                        <tr>
                          <th>Student Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Joined Date</th>
                          <th>Status</th>
                          <th className="text-center">Mark Attendance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map(student => (
                          <tr 
                            key={student.id}
                            className={attendanceStatus[student.id] === 'absent' ? 'table-danger' : 'table-success'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStudentClick(student)}
                          >
                            <td>
                              <strong className="text-primary">
                                <FaEye className="me-1" size={12} />
                                {student.name}
                              </strong>
                            </td>
                            <td>{student.email}</td>
                            <td>{student.phone || 'N/A'}</td>
                            <td>{student.joined_date || 'N/A'}</td>
                            <td>{getStatusBadge(attendanceStatus[student.id])}</td>
                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                              <Form.Check
                                type="switch"
                                id={`attendance-${student.id}`}
                                checked={attendanceStatus[student.id] === 'present'}
                                onChange={() => toggleStatus(student.id)}
                                label={attendanceStatus[student.id] === 'present' ? 'Present' : 'Absent'}
                                className={attendanceStatus[student.id] === 'present' ? 'text-success' : 'text-danger'}
                                style={{ 
                                  transform: 'scale(1.2)',
                                  marginLeft: 'auto',
                                  marginRight: 'auto',
                                  display: 'inline-block'
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {!loading && students.length > 0 && (
                  <div className="mt-3 text-muted small">
                    <FaCalendarCheck className="me-1" />
                    Tip: Click on any student row to view their attendance history calendar.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {showModal && selectedStudent && (
        <StudentHistoryModal
          student={selectedStudent}
          batchId={selectedBatch}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default DailyAttendance;